import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Heading3, Label, Paragraph } from '@treams/treams-ui';

import Role from './Role';
import RoleEdge from './RoleEdge';
import RolePlaceholder from './RolePlaceholder';

type Props = {
	user?: graphql.User;
	refetch: () => Promise<unknown>;
};

export default function Managers({ user, refetch }: Props) {
	const { t } = useTranslation();

	return (
		<Flex alignItems="flex-end">
			<Box flexGrow={0} flexShrink={0} width={200}>
				<Heading3 mb={1}>{t('employees.settings.roles.managers.title')}</Heading3>
				{user ? (
					<Paragraph>
						{t('employees.settings.roles.managers.description', { name: user.firstName })}
					</Paragraph>
				) : (
					<Paragraph.Skeleton lines={3} />
				)}
			</Box>
			<Box flexGrow={1} flexShrink={1} />
			<Box flexGrow={0} flexShrink={0} width={180 * 2 + 8}>
				<Flex alignItems="flex-end">
					<Box mr={2} flexShrink={0} width={180}>
						<Label mb={1}>{t('employees.settings.roles.managers.departmentManagers')}</Label>
						{user ? (
							user.group.managers.edges.map(({ node, role }) => {
								return (
									<Box mb={1} key={node.id}>
										<Role
											inverse
											transitive
											me={user!}
											externalReferenceID={role.externalReferenceID as string | undefined}
											user={node as graphql.User}
											role={role as graphql.Role}
											refetch={refetch}
										/>
									</Box>
								);
							})
						) : (
							<Box mb={1}>
								<Role.Skeleton />
							</Box>
						)}
						<Box style={{ position: 'relative' }}>
							<RoleEdge from={[90, 8]} to={[90 + 36 + 16, 96]} dashed bend="out" />
						</Box>
					</Box>
					<Box flexShrink={0} width={180}>
						<Label mb={1}>{t('employees.settings.roles.managers.oneOnOne')}</Label>
						{user ? (
							<>
								<Box mb={1}>
									<RolePlaceholder type="user" user={user} inverse refetch={refetch} />
								</Box>
								{user.individualManagers.map((role) => (
									<Box mb={1} key={role.source!.id}>
										<Role
											inverse
											indirect={role.isIndirect}
											me={user!}
											user={role.source}
											role={role.role as graphql.Role}
											externalReferenceID={role.externalReferenceID as string | undefined}
											refetch={refetch}
										/>
									</Box>
								))}
							</>
						) : (
							<Box mb={1}>
								<Role.Skeleton />
							</Box>
						)}
						<Box style={{ position: 'relative' }}>
							<RoleEdge from={[90, 8]} to={[36, 96]} bend="out" />
						</Box>
					</Box>
				</Flex>
			</Box>
			<Box flexGrow={1} flexShrink={1} />
		</Flex>
	);
}
